import { useEffect, useRef } from "react";

/**
 * Lightweight visibility-aware polling hook.
 *
 * Calls `callback` every `intervalMs` while the browser tab is visible.
 * When the tab is hidden, polling pauses (saves CPU + bandwidth). When the
 * tab becomes visible again, the callback fires immediately so the user sees
 * fresh data without having to manually refresh.
 *
 * The callback is stored in a ref so the interval doesn't get torn down on
 * every render when the callback identity changes — only `intervalMs` resets
 * the timer.
 */
export function usePolling(callback, intervalMs = 15000) {
  const cbRef = useRef(callback);
  useEffect(() => { cbRef.current = callback; }, [callback]);

  useEffect(() => {
    let timer = null;
    const tick = () => { try { cbRef.current?.(); } catch { /* noop */ } };
    const start = () => {
      if (timer) return;
      timer = setInterval(tick, intervalMs);
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        tick();       // refresh immediately when tab regains focus
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);
}

/**
 * Returns true when an ISO timestamp is within `seconds` of now.
 * Used to drive the red "NEW" pulse on freshly-arrived items.
 */
export function isFresh(iso, seconds = 60) {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  return (Date.now() - t) < seconds * 1000;
}
